<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  onUnmounted,
  defineEmits,
} from 'vue';
import { useDebounceFn, useEventListener } from '@vueuse/core';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

// [VITE] [TODO] We are using vue-virtual-scroll for now, since that seemed the simplest way to migrate
// from the current one. But we should consider using tanstack virtual in the future
// https://tanstack.com/virtual/latest/docs/framework/vue/examples/variable
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import ChatListHeader from './ChatListHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationItem from './ConversationItem.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import IntersectionObserver from './IntersectionObserver.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { toast } from 'dashboard/composables/useToast';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import { sortComparator } from 'dashboard/store/modules/conversations/helpers';
import SearchAPI from 'dashboard/api/search';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const resolveAttributesModalRef = ref(null);
const conversationListRef = ref(null);
const conversationDynamicScroller = ref(null);

provide('contextMenuElementTarget', conversationDynamicScroller);

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ME);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showAdvancedFilters = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const showNonService = ref(false);
const showGroupsOnly = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');

// ── Busca inline: digitou, apareceu — sem abrir a busca avancada do topo.
// Procura entre TODAS as conversas (nome/telefone do contato e conteudo de
// mensagem), ignorando aba e filtros da lista.
const termoBusca = ref('');
const resultadosConversas = ref([]);
const resultadosMensagens = ref([]);
const buscandoInline = ref(false);
const buscaAtiva = computed(() => termoBusca.value.trim().length >= 2);

const executarBuscaInline = useDebounceFn(async () => {
  const q = termoBusca.value.trim();
  if (q.length < 2) return;
  try {
    const [conv, msgs] = await Promise.all([
      SearchAPI.conversations({ q }),
      SearchAPI.messages({ q }),
    ]);
    // resposta velha de um termo que ja mudou: descarta
    if (q !== termoBusca.value.trim()) return;
    resultadosConversas.value = (conv.data?.payload?.conversations || []).slice(
      0,
      12
    );
    resultadosMensagens.value = (msgs.data?.payload?.messages || []).slice(
      0,
      12
    );
  } catch {
    resultadosConversas.value = [];
    resultadosMensagens.value = [];
  } finally {
    buscandoInline.value = false;
  }
}, 300);

watch(termoBusca, () => {
  if (!buscaAtiva.value) {
    resultadosConversas.value = [];
    resultadosMensagens.value = [];
    buscandoInline.value = false;
    return;
  }
  buscandoInline.value = true;
  executarBuscaInline();
});

const abrirResultadoBusca = displayId => {
  if (!displayId) return;
  router.push(
    `/app/accounts/${currentAccountId.value}/conversations/${displayId}`
  );
  termoBusca.value = '';
};

const tempoResultado = ts => (ts ? shortTimestamp(dynamicTime(ts)) : '');
const inicialDe = nome => (nome || '?').trim().charAt(0).toUpperCase();
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed
const intersectionObserverOptions = computed(() => {
  return {
    root: conversationListRef.value,
    rootMargin: '100px 0px 100px 0px',
  };
});

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const ASSIGNEE_TAB_ICONS = {
  me: 'i-lucide-user',
  queue: 'i-lucide-list-ordered',
  all: 'i-lucide-inbox',
};

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    icon: ASSIGNEE_TAB_ICONS[key],
    count: conversationStats.value[countKey] || 0,
  }));
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const count = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  ).count;
  return count;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

// ── Arquitetura de contextos: o servidor e o dono da ordem/associacao ──
const chaveContexto = computed(() =>
  [
    activeAssigneeTab.value,
    activeStatus.value,
    props.conversationInbox || '',
    props.label || '',
    props.teamId || '',
    props.conversationType || '',
    activeSortBy.value,
    showNonService.value ? 1 : 0,
    showGroupsOnly.value ? 1 : 0,
  ].join('|')
);
const contexto = computed(() =>
  store.getters['conversationResults/getContext'](chaveContexto.value)
);
const conversaPorId = useMapGetter('getConversationsIndex');
const currentUserId = useMapGetter('getCurrentUserID');

// Faixa "fila parada" (resumo gravado pelo alerta a cada 5 min).
const queueStallBanner = computed(() => {
  if (activeAssigneeTab.value !== wootConstants.ASSIGNEE_TYPE.QUEUE) return '';
  const stall = conversationStats.value?.queueStall;
  if (!stall || !stall.count) return '';
  const minutes = stall.oldest_minutes || 0;
  let wait = `${minutes} min`;
  if (minutes >= 2880) wait = `${Math.floor(minutes / 1440)} d`;
  else if (minutes >= 120) wait = `${Math.floor(minutes / 60)} h`;
  return t('CHAT_LIST.QUEUE_STALL.BANNER', { count: stall.count, wait });
});

const conversationFilters = computed(() => {
  // A Fila é sempre "abertas" — ignora o filtro de status (fila não tem resolvida).
  const isQueueTab =
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.QUEUE;
  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType: activeAssigneeTab.value,
    status: isQueueTab ? wootConstants.STATUS_TYPE.OPEN : activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
    nonService: showNonService.value ? 'true' : undefined,
    is_group: !!showGroupsOnly.value,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    // O servidor mandou a ordem e a associacao deste contexto; o cliente
    // exibe. Guardas locais so para TRANSICOES ao vivo com semantica
    // identica a do servidor: status e posse da aba.
    const mapa = conversaPorId.value;
    const statusAtivo =
      activeAssigneeTab.value === 'queue'
        ? wootConstants.STATUS_TYPE.OPEN
        : activeStatus.value;
    contexto.value.ids.forEach(id => {
      const conversa = mapa.get(id);
      if (!conversa) return;
      if (statusAtivo !== 'all' && conversa.status !== statusAtivo) return;
      if (
        activeAssigneeTab.value === 'me' &&
        conversa.meta?.assignee?.id !== currentUserId.value
      )
        return;
      if (
        activeAssigneeTab.value === 'queue' &&
        conversa.meta?.queue_state !== 'queued'
      )
        return;
      localConversationList.push(conversa);
    });
    return localConversationList;
  }

  localConversationList = [...chatLists.value];

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  localConversationList.sort((a, b) =>
    sortComparator(a, b, activeSortBy.value)
  );

  return localConversationList;
});

// Tempo real na ordenacao (comportamento historico restaurado): mensagem
// em conversa listada ou conversa que passa a pertencer ao contexto vai
// pro TOPO na hora — espelho local exato do que o servidor devolveria na
// pagina 1 (last_activity_at desc). A pill "N novas" morreu: ela so cobria
// conversa NOVA (mensagem em conversa existente nunca disparava nada, a
// ordem ficava congelada ate um refetch) e media o topo pelo $el do
// scroller, que no mobile nem e o container que rola.
const ordenacaoAoVivo = computed(
  () =>
    !hasAppliedFiltersOrActiveFolders.value &&
    activeSortBy.value === 'last_activity_at_desc'
);

// Caixas por id, pra guarda de prioridade do tempo real.
const inboxesPorId = computed(
  () => new Map((inboxesList.value || []).map(i => [i.id, i]))
);

const promoverAoTopo = id => {
  store.dispatch('conversationResults/promoteToHead', {
    chave: chaveContexto.value,
    id,
  });
};

// Conversa que ENTRA no contexto (nova, ou update que muda a posse — ex.:
// atribuida ao agente com a aba Minhas aberta): mesmas guardas do render.
const aoEntrarConversa = payload => {
  const id = payload?.id;
  if (!id) return;
  const conversa =
    payload.meta !== undefined ? payload : conversaPorId.value.get(id);
  if (!conversa) return;
  if (
    props.conversationInbox &&
    Number(props.conversationInbox) !== conversa.inbox_id
  )
    return;
  // Caixa NAO prioritaria: so entra ao vivo na aba Minhas, aberta e
  // atribuida a mim — espelho exato da regra do finder. Sem esta guarda,
  // o tempo real vazaria a conversa pras outras abas ate o refresh.
  const caixa = inboxesPorId.value.get(conversa.inbox_id);
  if (caixa && caixa.service_inbox === false) {
    const donoId =
      payload.assignee !== undefined
        ? payload.assignee?.id
        : conversa.meta?.assignee?.id;
    const minhaAberta =
      activeAssigneeTab.value === 'me' &&
      donoId === currentUserId.value &&
      conversa.status === 'open';
    if (!minhaAberta) return;
  }
  const assigneeId =
    payload.assignee !== undefined
      ? payload.assignee?.id
      : conversa.meta?.assignee?.id;
  if (activeAssigneeTab.value === 'me' && assigneeId !== currentUserId.value)
    return;
  if (
    activeAssigneeTab.value === 'queue' &&
    conversa.meta?.queue_state !== 'queued'
  )
    return;
  promoverAoTopo(id);
};

const pararDeOuvir = store.subscribe(mutation => {
  if (!ordenacaoAoVivo.value) return;
  if (mutation.type === 'ADD_MESSAGE') {
    // Mensagem (recebida OU enviada) em conversa ja listada: sobe.
    const id = mutation.payload?.conversation_id;
    if (id && contexto.value.ids.includes(id)) promoverAoTopo(id);
    return;
  }
  if (mutation.type === 'ADD_CONVERSATION') {
    aoEntrarConversa(mutation.payload);
    return;
  }
  if (
    mutation.type === 'UPDATE_CONVERSATION' ||
    mutation.type === 'UPDATE_ASSIGNEE'
  ) {
    // So ENTRADA no contexto — update de etiqueta/status em conversa ja
    // listada nao mexe em last_activity, logo nao reordena.
    if (!contexto.value.ids.includes(mutation.payload?.id))
      aoEntrarConversa(mutation.payload);
  }
});
onUnmounted(() => pararDeOuvir());

const fimDoContexto = computed(() =>
  hasAppliedFiltersOrActiveFolders.value
    ? hasCurrentPageEndReached.value
    : contexto.value.endReached
);

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    fimDoContexto.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
  // [VITE] removing this since the library has changed
  // nextTick(() => {
  //   // Addressing a known issue in the virtual list library where dynamically added items
  //   // might not render correctly. This workaround involves a slight manual adjustment
  //   // to the scroll position, triggering the list to refresh its rendering.
  //   const virtualList = conversationListRef.value;
  //   const scrollToOffset = virtualList?.scrollToOffset;
  //   const currentOffset = virtualList?.getOffset() || 0;
  //   if (scrollToOffset) {
  //     scrollToOffset(currentOffset + 1);
  //   }
  // });
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, and inboxes we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

const buscandoPagina = ref(false);

async function buscarPagina(inicial = false) {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  if (buscandoPagina.value) return;
  if (!inicial && contexto.value.endReached) return;
  buscandoPagina.value = true;
  const chave = chaveContexto.value;
  try {
    const params = { ...conversationFilters.value };
    params.page = inicial ? 1 : (contexto.value.pagina || 1) + 1;
    const cursor =
      !inicial && activeSortBy.value === 'last_activity_at_desc'
        ? contexto.value.cursor
        : null;
    if (cursor) {
      params.beforeLastActivity = cursor.ts;
      params.beforeId = cursor.id;
    }
    store.dispatch('updateChatListFilters', params);
    const itensAntes = contexto.value.ids?.length || 0;
    const payload = await store.dispatch('fetchConversationsPage', params);
    // contexto mudou durante a viagem: descartar — mas NUNCA em silencio
    // (hipotese 2 da saga; instrumentado pra debug em tela no mobile)
    if (chave !== chaveContexto.value) {
      // eslint-disable-next-line no-console
      console.warn('[lista] pagina descartada: chave flapou', {
        antiga: chave,
        nova: chaveContexto.value,
      });
      return;
    }
    const ultimo = payload[payload.length - 1];
    store.dispatch('conversationResults/upsertPage', {
      chave,
      ids: payload.map(c => c.id),
      cursor: ultimo
        ? {
            ts: ultimo.last_activity_at || ultimo.timestamp,
            id: ultimo.id,
          }
        : null,
      fim: payload.length < 25,
      substituirCabeca: inicial,
      pagina: params.page,
    });
    // Pagina cheia que nao ANEXOU nada = so repetidas (cursor nao andou?)
    const itensDepois = contexto.value.ids?.length || 0;
    // Divergencia contexto vs RENDER: ids chegaram mas a tela nao mostra
    // (filtro client-side descartando itens das paginas fundas)
    const renderizados = conversationList.value?.length || 0;
    if (!inicial && itensDepois - renderizados > 30) {
      // eslint-disable-next-line no-console
      console.warn('[lista] contexto > render', {
        ctx: itensDepois,
        render: renderizados,
        pagina: params.page,
      });
    }
    if (!inicial && payload.length > 0 && itensDepois === itensAntes) {
      // eslint-disable-next-line no-console
      console.warn('[lista] pagina so com repetidas', {
        pagina: params.page,
        recebidos: payload.length,
        cursor: contexto.value.cursor,
      });
    }
    emitConversationLoaded();
  } catch (erro) {
    // NUNCA engolir: um registro envenenado numa pagina especifica travava
    // o carregamento em silencio ("chega num ponto e para")
    // eslint-disable-next-line no-console
    console.error('[lista] falha ao processar pagina de conversas:', erro);
    toast.error('Falha ao carregar mais conversas — detalhe no console (F12).');
  } finally {
    buscandoPagina.value = false;
  }
}

function fetchConversations() {
  buscarPagina(true);
}

// Gatilho primario por POSICAO: o sensor de visibilidade so dispara em
// transicao e tinha janelas mortas (remonta fora da tela, recorte, timing).
// Posicao nao tem janela: todo scroll perto do fundo pede a proxima.
// Ancora fisica do fim da lista (wrapper do slot #after do scroller)
const rodapeListaEl = ref(null);

// "Perto do fundo" medido NA HORA — usado pelo gatilho de scroll e
// re-testado pelo trailing do throttle (ver loadMoreConversations).
const pertoDoFundo = () => {
  // Mede a POSICAO DO RODAPE NA TELA — independe de qual elemento rola.
  // No layout mobile o $el do DynamicScroller NAO e o container que rola
  // (um ancestral rola), entao o calculo por scrollTop nunca acusava
  // "perto do fundo" e o gatilho por posicao jamais disparava no celular
  // (sobrava so o sentinel, que morre na profundidade — a trava eterna).
  const rodape = rodapeListaEl.value;
  if (rodape) {
    return rodape.getBoundingClientRect().top - window.innerHeight < 600;
  }
  const el = conversationDynamicScroller.value?.$el;
  if (!el) return false;
  return el.scrollHeight - el.scrollTop - el.clientHeight < 600;
};

// Scroll no WINDOW com CAPTURE: pega o scroll de QUALQUER container
// (scroll interno nao borbulha, mas capture desce por todos).
useEventListener(
  window,
  'scroll',
  () => {
    // teto de auto-carregamento vale aqui tambem: sem ele o gatilho por
    // posicao emendava pagina atras de pagina ate o fim das 12k conversas
    // (o v-if do sensor nao protege este caminho)
    // eslint-disable-next-line no-use-before-define
    if (autoCarregamentoEsgotado.value) return;
    if (pertoDoFundo()) {
      // eslint-disable-next-line no-use-before-define
      loadMoreConversations({ dePosicao: true });
    }
  },
  { passive: true, capture: true }
);

function resetAndFetchData() {
  // Reinicia os contadores de pagina: o filtro novo define outro universo —
  // continuar da pagina antiga pulava direto pro "vazio" e marcava fim
  // sem nunca ter mostrado o comeco. As conversas em cache ficam (upsert
  // dedupa); so a paginacao recomeca do 1.
  store.dispatch('conversationPage/reset');
  appliedFilter.value = [];
  resetBulkActions();
  const elScroller = conversationDynamicScroller.value?.$el;
  if (elScroller) elScroller.scrollTop = 0;
  // Don't clear conversations from store — the conversationList computed
  // already filters by status/assignee/inbox client-side, so stale conversations
  // are hidden automatically. Keeping them avoids a flash of empty list and
  // prevents re-fetching data that's already in memory when switching back.
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

const debouncedResetAndFetch = useDebounceFn(resetAndFetchData, 300);

// Rajada dos logs: parado no fundo com status Todas (12k = 486 paginas), o
// sentinela emendava UMA BUSCA POR PAGINA ate o fim (469→489 seguidas nos
// logs) — piscar continuo e store inchando ate travar. Auto-scroll agora
// para em 500 conversas; dali em diante o "Carregar mais" e clique.
const LIMITE_AUTO_CARREGAMENTO = 500;
const autoCarregamentoEsgotado = computed(
  () =>
    !fimDoContexto.value &&
    // conta o CONTEXTO (ids do servidor), nao o render: o render capado
    // pelo LRU ficava em 150 e o teto nunca armava (busca infinita)
    (contexto.value.ids?.length || 0) >= LIMITE_AUTO_CARREGAMENTO
);
let ultimoLoadMore = 0;
let loadMoreAgendado = null;

function loadMoreConversations({ dePosicao = false } = {}) {
  if (fimDoContexto.value || chatListLoading.value || buscandoPagina.value) {
    return;
  }
  // Chamada vinda do gatilho por POSICAO precisa continuar perto do fundo
  // NA HORA de disparar: no celular o bounce do fim da lista cospe uma
  // rajada de eventos de scroll e o trailing abaixo emendava pagina atras
  // de pagina sem re-checar nada (o loop "carregando sem parar" do mobile).
  // Depois que uma pagina anexa ~1900px, este teste corta a corrente.
  if (dePosicao && !pertoDoFundo()) return;
  // Throttle com TRAILING: engolir o evento (return seco) perdia o gesto
  // para sempre — o observer so re-dispara em TRANSICAO de visibilidade, e
  // com resposta rapida (<400ms, tipico com filtro) o refire caia dentro
  // da janela e a lista "travava cedo". Agora o evento e adiado, nao
  // descartado.
  const agora = Date.now();
  if (agora - ultimoLoadMore < 400) {
    if (!loadMoreAgendado) {
      loadMoreAgendado = setTimeout(
        () => {
          loadMoreAgendado = null;
          loadMoreConversations({ dePosicao });
        },
        400 - (agora - ultimoLoadMore)
      );
    }
    return;
  }
  ultimoLoadMore = agora;

  if (!hasAppliedFiltersOrActiveFolders.value) {
    buscarPagina(false);
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// O gatilho por scroll morreu: `scroller.hasScrollbar` nunca existiu na
// lib (sempre undefined), entao esse caminho jamais disparou. O load-more
// e responsabilidade unica do IntersectionObserver no fim da lista.

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    const el = conversationDynamicScroller.value?.$el;
    if (el) el.scrollTop = 0;
    buscarPagina(true);
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function onToggleNonService() {
  showNonService.value = !showNonService.value;
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchConversations();
}

function onToggleGroups() {
  showGroupsOnly.value = !showGroupsOnly.value;
  // Must clear conversations from memory — groups and normal convos are mutually exclusive
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  fetchConversations();
}

function onResetView() {
  store.dispatch('clearConversationFilters');
  router.push({ name: 'home' });
}

function onInboxSelect(caixaEscolhida) {
  if (
    !caixaEscolhida ||
    Number(props.conversationInbox) === caixaEscolhida.id
  ) {
    router.push({ name: 'home' });
  } else {
    router.push({
      name: 'inbox_dashboard',
      params: { inbox_id: caixaEscolhida.id },
    });
  }
}

function onLabelSelect(label) {
  if (!label || props.label === label.title) {
    router.push({ name: 'home' });
  } else {
    router.push({
      name: 'label_conversations',
      params: { label: label.title },
    });
  }
}

function onFolderSelect(folder) {
  if (!folder || Number(props.foldersId) === folder.id) {
    router.push({ name: 'home' });
  } else {
    router.push({
      name: 'folder_conversations',
      params: { id: folder.id },
    });
  }
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    toast.success(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    toast.success(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    toast.error(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    toast.success(t('CONVERSATION.CHANGE_STATUS'));
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
});

onMounted(() => {
  // Link vindo do alerta "fila parada" abre direto na aba Fila.
  if (route.query?.assignee_type === wootConstants.ASSIGNEE_TYPE.QUEUE) {
    activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.QUEUE;
  }
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    toast.success(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    toast.error(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

watch(activeTeam, () => debouncedResetAndFetch());

watch(
  computed(() => props.conversationInbox),
  () => debouncedResetAndFetch()
);
watch(
  computed(() => props.label),
  () => debouncedResetAndFetch()
);
watch(
  computed(() => props.conversationType),
  () => debouncedResetAndFetch()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  debouncedResetAndFetch();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 conversations-list-wrap bg-[#EDEAE5] dark:bg-[#161312]"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout
        ? 'basis-full'
        : 'w-[300px] xl:w-[340px] 2xl:w-[412px]',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      :inboxes="inboxesList"
      :labels="labels"
      :folders="folders"
      :active-inbox-id="conversationInbox"
      :active-label="label"
      :active-folder-id="foldersId"
      :show-non-service="showNonService"
      :show-groups-only="showGroupsOnly"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @inbox-select="onInboxSelect"
      @label-select="onLabelSelect"
      @folder-select="onFolderSelect"
      @reset-view="onResetView"
      @toggle-non-service="onToggleNonService"
      @toggle-groups="onToggleGroups"
    >
      <template #busca>
        <!-- Busca inline: pilula sem borda (preenchimento suave), na linha
             dos botoes; o input e pelado na marra (CSS global de input traz
             caixa propria) e o foco acende o fundo, nao um anel -->
        <div
          class="flex h-7 w-full min-w-0 items-center gap-2 rounded-full bg-n-alpha-2 px-3 transition-colors focus-within:bg-n-alpha-3 dark:bg-n-solid-3 dark:focus-within:bg-n-solid-2"
        >
          <span class="i-lucide-search size-3.5 flex-shrink-0 text-n-slate-9" />
          <input
            v-model="termoBusca"
            type="text"
            class="!mb-0 !h-auto w-full !rounded-none !border-0 !bg-transparent !p-0 text-xs !shadow-none !outline-none !ring-0 placeholder:text-n-slate-9 focus:!ring-0"
            :placeholder="$t('CHAT_LIST.LIST_SEARCH.PLACEHOLDER')"
          />
          <button
            v-if="termoBusca"
            class="flex-shrink-0 text-n-slate-9 hover:text-n-slate-12"
            @click="termoBusca = ''"
          >
            <span class="i-lucide-x size-3.5 block" />
          </button>
        </div>
      </template>
    </ChatListHeader>

    <div
      v-if="queueStallBanner"
      class="flex items-start gap-2 px-4 py-2 text-xs font-medium text-n-ruby-11 bg-n-ruby-3 border-b border-n-ruby-6"
    >
      <span class="i-lucide-alert-triangle size-4 flex-shrink-0" />
      <span>{{ queueStallBanner }}</span>
    </div>

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <ChatTypeTabs
      v-if="!hasAppliedFiltersOrActiveFolders && !buscaAtiva"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      is-compact
      @chat-tab-change="updateAssigneeTab"
    />

    <p
      v-if="!chatListLoading && !conversationList.length && !buscaAtiva"
      class="flex overflow-auto justify-center items-center p-4 text-[13px] text-n-stone-300"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <ConversationBulkActions
      v-if="selectedConversations.length"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="toggleSelectAll"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />
    <!-- Resultados da busca inline -->
    <div
      v-if="buscaAtiva"
      class="flex-1 overflow-y-auto slim-scrollbar px-2 py-2"
    >
      <div v-if="buscandoInline" class="flex justify-center my-4">
        <Spinner class="text-n-brand" />
      </div>
      <template v-else>
        <p
          v-if="!resultadosConversas.length && !resultadosMensagens.length"
          class="p-4 text-center text-[12px] text-n-slate-9"
        >
          {{ $t('CHAT_LIST.LIST_SEARCH.EMPTY') }}
        </p>
        <div v-if="resultadosConversas.length" class="mb-2">
          <p
            class="mb-1 px-1 text-[10px] font-bold uppercase tracking-[0.06em] text-n-slate-9"
          >
            {{ $t('CHAT_LIST.LIST_SEARCH.CONVERSATIONS') }}
          </p>
          <div class="flex flex-col gap-1">
            <button
              v-for="resultado in resultadosConversas"
              :key="`c-${resultado.id}`"
              class="flex w-full items-center gap-2.5 rounded-xl border border-n-weak bg-white px-3 py-2 text-left transition-colors hover:border-n-slate-6 dark:border-[#3B3530] dark:bg-[#2A2522]"
              @click="abrirResultadoBusca(resultado.id)"
            >
              <span
                class="grid size-8 flex-shrink-0 place-content-center rounded-full bg-n-slate-4 text-[12px] font-bold text-n-slate-11"
              >
                {{ inicialDe(resultado.contact?.name) }}
              </span>
              <span class="min-w-0 flex-1">
                <span
                  class="block truncate text-[12.5px] font-semibold text-n-slate-12"
                >
                  {{ resultado.contact?.name || `#${resultado.id}` }}
                </span>
                <span class="block truncate text-[10.5px] text-n-slate-9">
                  {{ resultado.contact?.phone_number || resultado.inbox?.name }}
                </span>
              </span>
              <span
                class="flex-shrink-0 text-[10px] tabular-nums text-n-slate-9"
              >
                {{ tempoResultado(resultado.last_activity_at) }}
              </span>
            </button>
          </div>
        </div>
        <div v-if="resultadosMensagens.length">
          <p
            class="mb-1 px-1 text-[10px] font-bold uppercase tracking-[0.06em] text-n-slate-9"
          >
            {{ $t('CHAT_LIST.LIST_SEARCH.MESSAGES') }}
          </p>
          <div class="flex flex-col gap-1">
            <button
              v-for="mensagem in resultadosMensagens"
              :key="`m-${mensagem.id}`"
              class="flex w-full flex-col gap-0.5 rounded-xl border border-n-weak bg-white px-3 py-2 text-left transition-colors hover:border-n-slate-6 dark:border-[#3B3530] dark:bg-[#2A2522]"
              @click="abrirResultadoBusca(mensagem.conversation_id)"
            >
              <span class="flex w-full items-baseline gap-2">
                <span
                  class="min-w-0 flex-1 truncate text-[11.5px] font-semibold text-n-slate-12"
                >
                  {{ mensagem.sender?.name || `#${mensagem.conversation_id}` }}
                </span>
                <span
                  class="flex-shrink-0 text-[10px] tabular-nums text-n-slate-9"
                >
                  {{ tempoResultado(mensagem.created_at) }}
                </span>
              </span>
              <span class="w-full truncate text-[11.5px] text-n-slate-10">
                {{ mensagem.content }}
              </span>
            </button>
          </div>
        </div>
      </template>
    </div>

    <div
      v-else
      ref="conversationListRef"
      class="overflow-hidden flex-1 conversations-list hover:overflow-y-auto slim-scrollbar relative"
      :class="{ 'overflow-hidden': isContextMenuOpen }"
    >
      <DynamicScroller
        ref="conversationDynamicScroller"
        :items="conversationList"
        :min-item-size="78"
        class="overflow-auto w-full h-full slim-scrollbar py-1"
      >
        <template #default="{ item, index, active }">
          <!--
            If we encounter resizing issues, we can set the `watchData` prop to true
            this will deeply watch the entire object instead of just size dependencies
            But it can impact performance
          -->
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
            :size-dependencies="[item.uuid]"
          >
            <div class="py-0.5">
              <ConversationItem
                :source="item"
                :label="label"
                :team-id="teamId"
                :folders-id="foldersId"
                :conversation-type="conversationType"
                :show-assignee="showAssigneeInConversationCard"
                @select-conversation="selectConversation"
                @de-select-conversation="deSelectConversation"
              />
            </div>
          </DynamicScrollerItem>
        </template>
        <template #after>
          <!-- O rodape NAO troca de componente por pagina: antes o spinner
               (flag global) substituia o observador, que remontava ao fim
               de cada busca e redisparava — pisca-pisca de ~2/s no fundo.
               Agora o observador fica montado e o spinner e um irmao.
               O wrapper com ref e a ancora FISICA do "perto do fundo". -->
          <div ref="rodapeListaEl">
            <div
              v-if="buscandoPagina || chatListLoading"
              class="flex justify-center my-3"
            >
              <Spinner class="text-n-brand" />
            </div>
            <p
              v-if="showEndOfListMessage"
              class="p-4 text-center text-n-stone-300 text-[12px] flex items-center justify-center gap-1.5"
            >
              {{ $t('CHAT_LIST.EOF') }}
              <svg
                class="size-3.5 inline-block"
                viewBox="0 0 80 80"
                fill="none"
              >
                <defs>
                  <linearGradient
                    id="eof-logo-grad"
                    x1="0"
                    y1="0"
                    x2="80"
                    y2="80"
                    gradientUnits="userSpaceOnUse"
                  >
                    <!-- eslint-disable vue/no-static-inline-styles -->
                    <stop
                      offset="0%"
                      style="stop-color: rgb(var(--brand-primary))"
                    />
                    <stop
                      offset="100%"
                      style="stop-color: rgb(var(--brand-secondary))"
                    />
                    <!-- eslint-enable vue/no-static-inline-styles -->
                  </linearGradient>
                </defs>
                <path
                  d="M20 50c0-11.046 8.954-20 20-20s20 8.954 20 20"
                  stroke="url(#eof-logo-grad)"
                  stroke-width="5"
                  stroke-linecap="round"
                  opacity="0.7"
                />
                <path
                  d="M28 50c0-6.627 5.373-12 12-12s12 5.373 12 12"
                  stroke="url(#eof-logo-grad)"
                  stroke-width="5"
                  stroke-linecap="round"
                  opacity="0.5"
                />
                <path
                  d="M36 50c0-2.209 1.791-4 4-4s4 1.791 4 4"
                  stroke="url(#eof-logo-grad)"
                  stroke-width="5"
                  stroke-linecap="round"
                  opacity="0.3"
                />
              </svg>
            </p>
            <button
              v-if="autoCarregamentoEsgotado && !showEndOfListMessage"
              class="mx-auto my-3 flex items-center gap-2 rounded-lg border border-n-weak bg-white px-4 py-2 text-xs font-semibold text-n-slate-11 hover:border-n-slate-6 dark:bg-n-solid-3"
              @click="loadMoreConversations"
            >
              <span class="i-lucide-chevrons-down size-3.5" />
              {{ $t('CHAT_LIST.LOAD_MORE') }}
            </button>
            <!-- :key remonta SO o sensor a cada crescimento da lista: montado
               fixo ele nunca re-dispara (so avisa em transicao de
               visibilidade) e o carregamento parava apos uma pagina. O
               spinner e irmao, entao nada pisca. -->
            <IntersectionObserver
              v-if="!showEndOfListMessage && !autoCarregamentoEsgotado"
              :key="conversationList.length"
              :options="intersectionObserverOptions"
              @observed="loadMoreConversations"
            />
          </div>
        </template>
      </DynamicScroller>
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
